/**
 * 租户主题配置 API
 * 对应 foundation-service (7090)
 */

import { unifiedApi } from '@/utils/unifiedApi';

/** API响应类型 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/** 租户主题配置信息 (使用camelCase，因为request.ts会自动转换) */
export interface TenantThemeConfig {
  id: number;
  tenantId: number;

  // 基础主题色
  primaryColor: string;
  secondaryColor?: string;
  successColor: string;
  warningColor: string;
  errorColor: string;

  // 明暗模式
  themeMode: 'light' | 'dark' | 'auto';

  // Logo与背景
  logoUrl?: string;
  logoCollapsedUrl?: string;
  faviconUrl?: string;
  loginBgUrl?: string;
  dashboardBgUrl?: string;

  // 透明度设置 (0-100)
  sidebarOpacity: number;
  headerOpacity: number;
  cardOpacity: number;

  // 样式设置
  borderRadius: number;
  fontSize: number;
  compactMode: boolean;

  // 高级设置
  customCss?: string;
  extraConfig?: Record<string, unknown>;

  // REQ-20260508-0001 #10186: 登录页定制
  displayName?: string;
  subtitle?: string;
  loginLabel?: string;
  portalVisibility?: { admin?: boolean; mall?: boolean };

  // 审计字段
  status: number;
  createdAt: string;
  updatedAt: string;
}

/** 更新主题配置请求 (使用camelCase，request.ts会自动转换为snake_case发送给后端) */
export interface UpdateTenantThemeRequest {
  primaryColor?: string;
  secondaryColor?: string;
  successColor?: string;
  warningColor?: string;
  errorColor?: string;
  themeMode?: 'light' | 'dark' | 'auto';
  logoUrl?: string;
  logoCollapsedUrl?: string;
  faviconUrl?: string;
  loginBgUrl?: string;
  dashboardBgUrl?: string;
  sidebarOpacity?: number;
  headerOpacity?: number;
  cardOpacity?: number;
  borderRadius?: number;
  fontSize?: number;
  compactMode?: boolean;
  customCss?: string;
  extraConfig?: Record<string, unknown>;

  // REQ-20260508-0001 #10186: 登录页定制字段
  displayName?: string;       // 登录页显示名（如 "甲品牌经销商门户"）
  subtitle?: string;          // 登录页副标题
  loginLabel?: string;        // 账号字段标签（如 "账号" / "经销商编码" / "手机号"）
  portalVisibility?: { admin?: boolean; mall?: boolean };
}

/** 预设主题信息 (使用camelCase，因为request.ts会自动转换) */
export interface PresetTheme {
  code: string;
  name: string;
  primaryColor: string;
  themeMode: 'light' | 'dark' | 'auto';
  borderRadius: number;
  /** REQ-20260619-0002: 强调色（森林绿预设的橙色 CTA） */
  secondaryColor?: string;
}

/**
 * 从 unifiedApi 响应中提取后端数据
 *
 * unifiedApi.service().get() 返回格式:
 * { success: true, code: 200, data: <后端原始响应>, message: '操作成功' }
 *
 * 后端原始响应格式:
 * { code: 0, message: "success", data: <实际数据> }
 */
function extractBackendResponse<T>(response: any): { code: number; message: string; data: T } {
  // response.data 是后端返回的完整响应 {code, message, data}
  const backendResp = response.data;

  // 如果 backendResp 有 code 字段，说明是标准后端响应格式 {code, message, data}
  if (backendResp && typeof backendResp.code === 'number') {
    return {
      code: backendResp.code,
      message: backendResp.message ?? '',
      data: backendResp.data as T,
    };
  }

  // 否则 response.data 就是直接的数据（unifiedApi 已经解包了）
  // 此时 response.success 表示请求是否成功
  return {
    code: response.success ? 0 : (response.code ?? 500),
    message: response.message ?? '',
    data: backendResp as T,
  };
}

/** 获取当前租户主题配置 */
export const getTenantTheme = async (): Promise<ApiResponse<TenantThemeConfig>> => {
  const response = await unifiedApi.service('system').get<any>('/tenant-theme');
  const backend = extractBackendResponse<TenantThemeConfig>(response);
  return {
    success: backend.code === 0,
    data: backend.data,
    message: backend.message,
  };
};

/** 更新当前租户主题配置 */
export const updateTenantTheme = async (data: UpdateTenantThemeRequest): Promise<ApiResponse<TenantThemeConfig>> => {
  const response = await unifiedApi.service('system').put<any>('/tenant-theme', data);
  const backend = extractBackendResponse<TenantThemeConfig>(response);
  return {
    success: backend.code === 0,
    data: backend.data,
    message: backend.message,
  };
};

/** 重置租户主题配置为默认值 */
export const resetTenantTheme = async (): Promise<ApiResponse<TenantThemeConfig>> => {
  const response = await unifiedApi.service('system').post<any>('/tenant-theme/reset');
  const backend = extractBackendResponse<TenantThemeConfig>(response);
  return {
    success: backend.code === 0,
    data: backend.data,
    message: backend.message,
  };
};

/** 获取预设主题列表 */
export const getPresetThemes = async (): Promise<ApiResponse<PresetTheme[]>> => {
  const response = await unifiedApi.service('system').get<any>('/tenant-theme/presets');
  const backend = extractBackendResponse<PresetTheme[]>(response);
  return {
    success: backend.code === 0,
    data: backend.data,
    message: backend.message,
  };
};

/** 应用预设主题 */
export const applyPresetTheme = async (presetCode: string): Promise<ApiResponse<TenantThemeConfig>> => {
  const response = await unifiedApi.service('system').post<any>('/tenant-theme/apply-preset', {
    preset_code: presetCode,
  });
  const backend = extractBackendResponse<TenantThemeConfig>(response);
  return {
    success: backend.code === 0,
    data: backend.data,
    message: backend.message,
  };
};

/** 公开获取租户主题配置 (登录页使用，无需认证) */
export const getPublicTenantTheme = async (tenantId: number): Promise<ApiResponse<TenantThemeConfig>> => {
  const response = await unifiedApi.get<any>(`/api/v1/public/tenant-theme/${tenantId}`);
  const backend = extractBackendResponse<TenantThemeConfig>(response);
  return {
    success: backend.code === 0,
    data: backend.data,
    message: backend.message,
  };
};

/** 租户主题 API 对象 */
export const tenantThemeApi = {
  getTenantTheme,
  updateTenantTheme,
  resetTenantTheme,
  getPresetThemes,
  applyPresetTheme,
  getPublicTenantTheme,
};

export default tenantThemeApi;
